

































import {
	computed,
	defineComponent,
	PropType,
	Ref,
	ref,
} from '@vue/composition-api';
import { Cropper } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';
import CtaButton from '@front-app/components/buttons/CtaButton.vue';
import { ButtonPalettes } from '@front-app/components/buttons/palettes';
import ErrorCard from '@front-app/components/cards/state/ErrorCard.vue';
import { Size } from '@front-app/components/constants';
import TrModal from '@front-app/components/modals/TrModal.vue';
import { useAsyncStatus } from '@front-app/hooks/useAsyncStatus';
import { EventImageType } from '@front-app/services/EventsApi/EventModel';
import { updateEventLogo, updateEventBanner, updateEventVenueFloorPlan } from '@front-app/services/EventsApi/EventsApi';
import { ImageScale } from '@front-app/services/ImageApi/ImageModel';
import { EventsBannerStore, EventsLogoStore, EventsVenueStore } from '@front-app/store/singletons/Events';
import { blobToFile } from '@front-app/util/image';

const ASPECT_RATIO_FOR_TYPE: Record<EventImageType, number> = {
	[EventImageType.LOGO]: 1 / 1,
	[EventImageType.BANNER]: 16 / 9,
	[EventImageType.VENUE]: 3 / 1,
};

type EventImageUpdater = (eventId: string, blob: Blob) => Promise<void>;

const IMAGE_UPDATER_FOR_TYPE: Record<EventImageType, EventImageUpdater> = {
	[EventImageType.LOGO]: async (eventId: string, blob: Blob): Promise<void> => {
		await updateEventLogo(eventId, blobToFile(blob, 'event-logo'));
		await EventsLogoStore.invalidate(eventId, ImageScale.LARGE_2X);
	},
	[EventImageType.BANNER]: async (eventId: string, blob: Blob): Promise<void> => {
		await updateEventBanner(eventId, blobToFile(blob, 'event-banner'));
		await EventsBannerStore.invalidate(eventId, ImageScale.BANNER);
	},
	[EventImageType.VENUE]: async (eventId: string, blob: Blob): Promise<void> => {
		await updateEventVenueFloorPlan(eventId, blobToFile(blob, 'event-venue'));
		await EventsVenueStore.invalidate(eventId, ImageScale.VENUE);
	},
};

export default defineComponent({
	components: {
		Cropper,
		CtaButton,
		ErrorCard,
		TrModal,
	},
	props: {
		eventId: {
			type: String,
			required: true,
		},
		type: {
			type: String as PropType<EventImageType>,
			required: true,
		},
		imageUrl: {
			type: String,
			required: true,
		},
		mimeType: {
			type: String,
			required: true,
		},
		isOpen: Boolean,
	},
	setup(props, ctx) {
		const cropper: Ref<null | Cropper> = ref(null);

		const aspectRatio = computed(() => ASPECT_RATIO_FOR_TYPE[props.type]);

		const {
			track,
			isError,
			isLoading,
			isSuccess,
		} = useAsyncStatus();
		const saveEventImage = async () => {
			const cropperVal = cropper.value;
			if (!cropperVal) {
				return;
			}
			const { canvas } = cropperVal.getResult();
			canvas.toBlob(async (blob) => {
				if (!blob) {
					return;
				}

				await track(IMAGE_UPDATER_FOR_TYPE[props.type](props.eventId, blob));

				if (isSuccess.value) {
					ctx.emit('close');
				}
			}, props.mimeType);
		};
		return {
			aspectRatio,
			saveEventImage,
			isError,
			isLoading,
			cropper,
			Size,
			ButtonPalettes,
		};
	},
});
